import React from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { CircleHelp, TriangleAlert, X } from 'lucide-react';
import { Button } from './Button';
import { ErrorState } from './ErrorState';
import { useEscapeToClose } from './useEscapeToClose';

/**
 * Asks before an action that cannot be taken back, or that reaches outside the
 * platform.
 *
 * Replaces `window.confirm` so those actions stay inside the platform's visual
 * language instead of a browser prompt.
 *
 * The colour of the confirm button follows the button guideline: red only when
 * the action destroys or replaces something, green for everything else — even
 * when it is irreversible. What it cannot undo, or who else it reaches, is said
 * in `consequences`, not in the colour.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {() => void} props.onConfirm
 * @param {string} props.title - Verb and object: "Eliminar bulto".
 * @param {React.ReactNode} props.message - How many, and what happens.
 * @param {string[]} [props.consequences] - Effects the user should know before
 *   confirming: who gets notified, what cannot be undone.
 * @param {'danger'|'primary'} [props.variant='danger']
 * @param {string} [props.confirmLabel='Eliminar']
 * @param {string} [props.loadingLabel='Eliminando...'] - Shown while `loading`.
 * @param {boolean} [props.loading=false]
 * @param {string|null} [props.error] - Why the action failed. The dialog stays
 *   open and says it, instead of closing on a failure the user never sees.
 * @param {string} [props.testId='confirm-dialog'] - Names the dialog and, with
 *   `-cancel`, `-confirm` and `-error`, its parts. A page with more than one
 *   confirmation needs to tell them apart.
 */
export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  consequences = [],
  variant = 'danger',
  confirmLabel = 'Eliminar',
  loadingLabel = 'Eliminando...',
  loading = false,
  error = null,
  testId = 'confirm-dialog',
}) => {
  const titleId = React.useId();
  useEscapeToClose(isOpen, onClose, loading);

  if (!isOpen) return null;

  const isDanger = variant === 'danger';
  const Icon = isDanger ? TriangleAlert : CircleHelp;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={loading ? undefined : onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={loading}
        data-testid={testId}
        data-variant={variant}
        className="relative bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-md p-6 shadow-2xl border border-border animate-in fade-in zoom-in-95 duration-200"
      >
        <Button
          variant="subtle"
          size="sm"
          iconOnly
          icon={X}
          onClick={onClose}
          disabled={loading}
          aria-label="Cerrar"
          className="absolute top-4 right-4"
        />

        <div
          className={clsx(
            'w-12 h-12 rounded-xl flex items-center justify-center mb-4',
            isDanger
              ? 'bg-red-50 dark:bg-red-900/20 text-danger'
              : 'bg-ninja-greenLight text-ninja-green'
          )}
        >
          <Icon size={22} />
        </div>

        <h3 id={titleId} className="text-xl font-bold text-ninja-dark dark:text-white mb-2">{title}</h3>
        <div className="text-sm text-text-secondary mb-4">{message}</div>

        {consequences.length > 0 && (
          <ul className="mb-4 space-y-1.5" data-testid={`${testId}-consequences`}>
            {consequences.map((consequence) => (
              <li key={consequence} className="flex items-start gap-2 text-sm text-text-secondary">
                <span aria-hidden="true" className="mt-2 w-1 h-1 rounded-full bg-text-muted flex-shrink-0" />
                <span>{consequence}</span>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <div className="mb-4" data-testid={`${testId}-error`}>
            <ErrorState variant="inline" title="No se pudo completar" description={error} />
          </div>
        )}

        <div className="flex items-center justify-end gap-3 mt-6">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
            data-testid={`${testId}-cancel`}
          >
            Cancelar
          </Button>
          <Button
            variant={isDanger ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            loadingLabel={loadingLabel}
            data-testid={`${testId}-confirm`}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
